import { createContext, useContext, useEffect, useRef, useState, type ReactNode, type RefObject } from "react"
import { PictureInPicture2 } from "lucide-react"
import { ActionHintTooltip } from "@/components/ui/action-hint-tooltip"
import { cacheDesktopMedia } from "@/lib/desktop"
import { useTranslation } from "@/hooks/use-translation"
import type { PlaybackTimeStore } from "@/lib/timeline"
import { pipKinds, pipTargetSec, samePipKinds, type PipFrame, type PipKind } from "@/lib/player-pip"
import { cn } from "@/lib/utils"

const SEEK_DRIFT_SEC = 0.12

type PlayerPipContextValue = {
  enabled: boolean
  videoRef: RefObject<HTMLVideoElement | null>
  choice: PipKind | null
  menuOpen: boolean
  available: readonly PipKind[]
  onButtonClick: () => void
  pick: (kind: PipKind) => void
  closeMenu: () => void
}

const PlayerPipContext = createContext<PlayerPipContextValue | null>(null)

function usePlayerPip(): PlayerPipContextValue | null {
  return useContext(PlayerPipContext)
}

/**
 * Один немой video на плеер. Время берётся из уже существующего timeStore:
 * подписчик не кладёт кадр в React state.
 */
export function PlayerPipProvider({
  resolve,
  timeStore,
  playing,
  getRate,
  children,
}: {
  resolve: ((timeMs: number) => PipFrame | null) | null
  timeStore: PlaybackTimeStore
  playing: boolean
  getRate: () => number
  children: ReactNode
}) {
  const enabled = resolve != null
  const resolveRef = useRef(resolve)
  resolveRef.current = resolve
  const playingRef = useRef(playing)
  playingRef.current = playing
  const getRateRef = useRef(getRate)
  getRateRef.current = getRate
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const seenMediaUrlsRef = useRef(new Set<string>())
  const [choice, setChoice] = useState<PipKind | null>(null)
  const choiceRef = useRef(choice)
  choiceRef.current = choice
  const [menuOpen, setMenuOpen] = useState(false)
  const [available, setAvailable] = useState<PipKind[]>([])
  const tickRef = useRef<() => void>(() => {})

  useEffect(() => {
    if (!enabled) {
      tickRef.current = () => {}
      return
    }
    const tick = () => {
      const resolved = resolveRef.current?.(timeStore.get()) ?? null
      const freshMedia: string[] = []
      for (const url of [resolved?.sources.previz?.url, resolved?.sources.animatic?.url]) {
        if (!url || seenMediaUrlsRef.current.has(url)) continue
        seenMediaUrlsRef.current.add(url)
        freshMedia.push(url)
      }
      if (freshMedia.length > 0) cacheDesktopMedia(freshMedia)
      const nextKinds = pipKinds(resolved?.sources)
      setAvailable((prev) => (samePipKinds(prev, nextKinds) ? prev : nextKinds))
      const video = videoRef.current
      const selected = choiceRef.current
      const clip = selected && resolved ? resolved.sources[selected] : null
      if (!video) return
      if (!clip) {
        if (!video.paused) video.pause()
        return
      }
      if (video.getAttribute("src") !== clip.url) {
        video.muted = true
        video.src = clip.url
        return
      }
      if (video.readyState < 1) return
      const rate = getRateRef.current()
      const { sec, freeze } = pipTargetSec(clip, resolved?.localMs ?? 0, video.duration)
      if (Math.abs(video.currentTime - sec) > SEEK_DRIFT_SEC) video.currentTime = sec
      const forward = playingRef.current && rate > 0 && !freeze
      const nextRate = forward ? rate : 1
      if (video.playbackRate !== nextRate) video.playbackRate = nextRate
      if (forward) {
        if (video.paused) void video.play().catch(() => {})
      } else if (!video.paused) {
        video.pause()
      }
    }
    tickRef.current = tick
    const video = videoRef.current
    video?.addEventListener("loadedmetadata", tick)
    tick()
    const unsubscribe = timeStore.subscribe(tick)
    return () => {
      tickRef.current = () => {}
      video?.removeEventListener("loadedmetadata", tick)
      unsubscribe()
    }
  }, [enabled, timeStore, choice])

  // Пауза не шлёт тик часов, поэтому тот же tick вызываем, когда playing уже записан в ref.
  useEffect(() => {
    tickRef.current()
  }, [playing])

  const onButtonClick = () => {
    if (available.length === 0) return
    if (available.length === 1) {
      const only = available[0]
      setMenuOpen(false)
      setChoice((current) => (current === only ? null : only))
      return
    }
    setMenuOpen((open) => !open)
  }

  const pick = (kind: PipKind) => {
    setChoice((current) => (current === kind ? null : kind))
    setMenuOpen(false)
  }

  const value: PlayerPipContextValue = {
    enabled,
    videoRef,
    choice,
    menuOpen,
    available,
    onButtonClick,
    pick,
    closeMenu: () => setMenuOpen(false),
  }

  return <PlayerPipContext.Provider value={value}>{children}</PlayerPipContext.Provider>
}

export function PlayerPipButton() {
  const pip = usePlayerPip()
  const { t } = useTranslation(["shot", "common"])
  const rootRef = useRef<HTMLDivElement>(null)
  const closeMenuRef = useRef<() => void>(() => {})
  closeMenuRef.current = pip?.closeMenu ?? (() => {})
  const menuOpen = pip?.menuOpen === true

  useEffect(() => {
    if (!menuOpen) return
    const onPointerDown = (event: PointerEvent) => {
      if (rootRef.current?.contains(event.target as Node)) return
      closeMenuRef.current()
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeMenuRef.current()
    }
    document.addEventListener("pointerdown", onPointerDown)
    document.addEventListener("keydown", onKey)
    return () => {
      document.removeEventListener("pointerdown", onPointerDown)
      document.removeEventListener("keydown", onKey)
    }
  }, [menuOpen])

  if (!pip?.enabled) return null

  const active = pip.choice != null && pip.available.includes(pip.choice)
  const disabled = pip.available.length === 0

  return (
    <div ref={rootRef} className="relative flex">
      <ActionHintTooltip label={t("player.pip")}>
        <button
          type="button"
          disabled={disabled}
          aria-pressed={active}
          aria-expanded={pip.available.length > 1 ? pip.menuOpen : undefined}
          onClick={pip.onButtonClick}
          className={cn(
            "rounded p-1.5 transition-colors disabled:cursor-not-allowed disabled:opacity-40",
            active
              ? "bg-ui-surface-raised text-ui-accent-text"
              : "text-ui-text-secondary hover:bg-ui-surface-raised hover:text-ui-text-primary",
          )}
        >
          <PictureInPicture2 className="h-4 w-4" />
        </button>
      </ActionHintTooltip>
      {pip.menuOpen && pip.available.length > 1 ? (
        <div
          role="menu"
          className="absolute bottom-full left-1/2 z-50 mb-1 flex -translate-x-1/2 flex-col overflow-hidden rounded-lg border border-ui-border bg-ui-surface py-1 shadow-lg"
        >
          {pip.available.map((kind) => (
            <button
              key={kind}
              type="button"
              role="menuitem"
              onClick={() => pip.pick(kind)}
              className={cn(
                "px-3 py-1.5 text-left text-xs whitespace-nowrap",
                pip.choice === kind
                  ? "bg-ui-surface-raised text-ui-accent-text"
                  : "text-ui-text-secondary hover:bg-ui-surface-raised hover:text-ui-text-primary",
              )}
            >
              {kind === "previz" ? t("player.pipPreviz") : t("player.pipAnimatic")}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}

export function PlayerPipOverlay() {
  const pip = usePlayerPip()
  if (!pip?.enabled || pip.choice == null) return null
  const visible = pip.available.includes(pip.choice)

  return (
    <div
      className={cn(
        "pointer-events-none absolute right-2 bottom-2 z-30 w-1/4 overflow-hidden rounded-lg border border-white/25 bg-black shadow-lg",
        visible ? "opacity-100" : "hidden",
      )}
    >
      <video ref={pip.videoRef} className="block w-full" muted playsInline preload="auto" />
    </div>
  )
}
